import React, {useContext, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import InfoIcon from '@material-ui/icons/Info';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import BarChartIcon from '@material-ui/icons/BarChart';
import ExtensionIcon from '@material-ui/icons/Extension';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import List from '@material-ui/core/List';
import Collapse from '@material-ui/core/Collapse';
import {Divider} from '@material-ui/core';
import {withRouter} from 'react-router';
import {useLocation} from 'react-router-dom';
import {AuthContext} from '../../../context/auth';
import {getPlanStage} from '../../../utils/productAccess';
import {DQ_PRO_PLAN_KEY} from "../../../utils/constants";

const ORANGE = '#FF6900';
const GREY = '#A5A5A5';

const OVERVIEW_BASE = '/dq_pro/partner-dashboard/overview';

// PbDQ Navigation Bar sheet, "P1 Partner Dashboard_13Aug2026" — Type of
// Dashboard: Partner Dashboard. Each leaf's `packages` is the sheet's
// "Packages" column (P0 / P1 / LC); a leaf with no `packages` is common to
// all three. Order below matches the sheet's Order column exactly.
const PARTNER_DASHBOARD_NAV = [
    {
        code: 'introduction',
        label: 'Introduction',
        icon: InfoIcon,
        base: OVERVIEW_BASE,
        children: [
            {label: 'About COS Readiness Solution', path: `${OVERVIEW_BASE}/about-readiness-solution`, packages: ['P1']},
            {label: 'About COS Children Assessment', path: `${OVERVIEW_BASE}/about-assessment`,            packages: ['P0', 'P1']},
            {label: 'About Scores',                  path: `${OVERVIEW_BASE}/about-rubrics-scores`,        packages: ['P0', 'P1', 'LC']},
            {label: 'About Partner Report',          path: `${OVERVIEW_BASE}/about-report`,                packages: ['P0', 'P1']},
        ],
    },
    {
        code: 'baseline_results',
        label: 'Baseline Results',
        icon: BarChartIcon,
        children: [
            {label: 'Portfolio Baseline', path: '/dq_pro/partner-dashboard/country-client',          packages: ['P0', 'P1']},
            {label: 'Cyber Risks',        path: '/dq_pro/partner-dashboard/risk-quality',             packages: ['P0', 'P1']},
            {label: 'Participation',      path: '/dq_pro/partner-dashboard/participation-coverage',   packages: ['P0', 'P1']},
        ],
    },
    {
        code: 'learning_programs',
        label: 'Learning Programs',
        icon: ExtensionIcon,
        children: [
            {label: 'Program Deployment', path: '/dq_pro/partner-dashboard/learning-deployment', packages: ['P1', 'LC']},
            {label: 'Learning Impact',    path: '/dq_pro/partner-dashboard/learning-impact',      packages: ['P1', 'LC']},
        ],
    },
    {
        code: 'reassessment',
        label: 'Reassessment',
        icon: TrendingUpIcon,
        path: '/dq_pro/partner-dashboard/reassessment',
        packages: ['P1'],
    },
    {
        code: 'methodology',
        label: 'Methodology',
        icon: MenuBookIcon,
        children: [
            {label: 'How the Assessment Is Scored',        path: '/dq_pro/partner-dashboard/methodology',                  packages: ['P0', 'P1']},
            {label: 'How Priority Actions Are Triggered',  path: '/dq_pro/partner-dashboard/methodology/priority-actions', packages: ['P0', 'P1']},
            {label: 'How Learning Impact Is Measured',     path: '/dq_pro/partner-dashboard/methodology/learning-impact',  packages: ['P1', 'LC']},
        ],
    },
];

// P0 is the base/default package — a Partner owner who hasn't resolved to a
// known plan_key still sees the P0 baseline rather than an empty sidebar.
const resolvePackage = (planKey) => {
    if (planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_P1) return 'P1';
    if (planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_LC) return 'LC';
    return 'P0';
};

const useStyles = makeStyles(() => ({
    item: {
        width: '92%',
        margin: '2px 10px',
        borderRadius: '10px',
        padding: '10px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    itemCollapsed: {
        width: '65%',
    },
    itemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    backItem: {
        backgroundColor: '#000000 !important',
        '& .MuiTypography-root': {color: '#ffffff'},
        '&:hover': {backgroundColor: '#222222 !important'},
    },
    subItem: {
        width: '85%',
        margin: '2px 10px 2px 32px',
        borderRadius: '10px',
        padding: '8px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    subItemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    icon: {minWidth: 'unset', marginRight: '12px'},
    label: {
        '& .MuiTypography-root': {fontSize: '14px', fontWeight: 600, color: 'inherit'},
    },
    divider: {margin: '4px 10px'},
}));

const PartnerDashboardList = ({history, isDrawerOpen}) => {
    const classes = useStyles();
    const location = useLocation();
    const {authUser} = useContext(AuthContext);
    const pkg = resolvePackage(authUser?.dq_pro_plan?.plan_key);
    const hasAccess = (item) => !item.packages || item.packages.includes(pkg);

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    const visibleGroups = PARTNER_DASHBOARD_NAV
        .map((group) => group.children
            ? {...group, children: group.children.filter(hasAccess)}
            : group)
        .filter((group) => (group.children ? group.children.length > 0 : hasAccess(group)));

    const [openGroups, setOpenGroups] = useState(() => {
        const initial = {};
        visibleGroups.forEach((group) => {
            if (group.children) {
                initial[group.code] = group.children.some((c) => isActive(c.path));
            }
        });
        return initial;
    });
    const toggleGroup = (code) => setOpenGroups((prev) => ({...prev, [code]: !prev[code]}));

    const NavItem = ({label, icon: Icon, path}) => {
        const active = isActive(path);
        return (
            <>
                <ListItem
                    button
                    onClick={() => history.push(path)}
                    className={`${classes.item} ${!isDrawerOpen ? classes.itemCollapsed : ''} ${active ? classes.itemActive : ''}`}
                >
                    <ListItemIcon className={classes.icon}>
                        <Icon style={{color: active ? '#ffffff' : ORANGE, fontSize: '22px'}}/>
                    </ListItemIcon>
                    {isDrawerOpen && <ListItemText className={classes.label} primary={label}/>}
                </ListItem>
                <Divider className={classes.divider}/>
            </>
        );
    };

    const SubItem = ({label, path, active}) => {
        return (
            <ListItem
                button
                onClick={() => history.push(path)}
                className={`${classes.subItem} ${active ? classes.subItemActive : ''}`}
            >
                {isDrawerOpen && <ListItemText className={classes.label} primary={label}/>}
            </ListItem>
        );
    };

    const NavGroup = ({group}) => {
        const active = group.children.some((c) => isActive(c.path));
        const open = !!openGroups[group.code];
        const Icon = group.icon;
        // Only the most specific match is highlighted: /methodology also prefixes
        // /methodology/priority-actions, which lit both items at once (slide 42).
        const activeChildPath = group.children
            .filter((c) => isActive(c.path))
            .reduce((best, c) => (!best || c.path.length > best.length ? c.path : best), null);
        return (
            <>
                <ListItem
                    button
                    onClick={() => isDrawerOpen
                        ? toggleGroup(group.code)
                        : history.push(group.children[0].path)}
                    className={`${classes.item} ${!isDrawerOpen ? classes.itemCollapsed : ''} ${active ? classes.itemActive : ''}`}
                >
                    <ListItemIcon className={classes.icon}>
                        <Icon style={{color: active ? '#ffffff' : ORANGE, fontSize: '22px'}}/>
                    </ListItemIcon>
                    {isDrawerOpen && (
                        <>
                            <ListItemText className={classes.label} primary={group.label}/>
                            {open
                                ? <ExpandLess style={{color: active ? '#ffffff' : ORANGE}}/>
                                : <ExpandMore style={{color: active ? '#ffffff' : ORANGE}}/>
                            }
                        </>
                    )}
                </ListItem>
                <Divider className={classes.divider}/>

                <Collapse in={open && isDrawerOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {group.children.map((c) => (
                            <SubItem key={c.path} label={c.label} path={c.path} active={c.path === activeChildPath}/>
                        ))}
                    </List>
                </Collapse>
            </>
        );
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden'}}>
            <List style={{flexGrow: 1, paddingTop: '8px', overflowY: 'auto'}}>
                {/* Back to Workspace */}
                <ListItem
                    button
                    onClick={() => history.push('/my_workspace')}
                    className={`${classes.item} ${classes.backItem} ${!isDrawerOpen ? classes.itemCollapsed : ''}`}
                >
                    <ListItemIcon className={classes.icon}>
                        <ArrowBackIcon style={{color: '#ffffff', fontSize: '22px'}}/>
                    </ListItemIcon>
                    {isDrawerOpen && <ListItemText className={classes.label} primary="Back to Workspace"/>}
                </ListItem>
                <Divider className={classes.divider}/>

                {visibleGroups.map((group) => group.children
                    ? <NavGroup key={group.code} group={group}/>
                    : <NavItem key={group.code} label={group.label} icon={group.icon} path={group.path}/>
                )}
            </List>
        </div>
    );
};

export default withRouter(PartnerDashboardList);
